import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

const COLLAPSED_ROWS = 3;
const EMPTY = /^(?:[-–—]+|n\/a|)$/i;

export interface ParsedTable {
  header: string[];
  rows: string[][];
}

const splitRow = (line: string): string[] =>
  line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((c) => c.trim());

export const isTableLine = (line: string): boolean => /^\s*\|.*\|\s*$/.test(line);
const isSeparator = (cells: string[]): boolean =>
  cells.length > 0 && cells.every((c) => /^:?-{3,}:?$/.test(c));

/** Parses consecutive `| a | b |` lines into a header plus body rows; null when not a table. */
export function parseTable(lines: string[]): ParsedTable | null {
  const cells = lines.map(splitRow);
  const sepIdx = cells.findIndex(isSeparator);
  if (sepIdx < 1) return null;
  const rows = cells.filter((_, i) => i !== sepIdx && i !== 0);
  if (!rows.length) return null;
  return { header: cells[0], rows };
}

interface AssistantTableProps {
  table: ParsedTable;
  renderInline: (text: string, key: string) => ReactNode[];
  keyPrefix: string;
}

/**
 * Stacked rows instead of a multi-column grid (the panel is ~420px wide): name and key value on
 * line 1, remaining non-empty columns joined with " · " as muted meta on line 2.
 */
export function AssistantTable({ table, renderInline, keyPrefix }: AssistantTableProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const total = table.rows.length;
  const canCollapse = total > COLLAPSED_ROWS;
  const visible = canCollapse && !expanded ? table.rows.slice(0, COLLAPSED_ROWS) : table.rows;
  const hasMeta = table.header.length > 2;

  return (
    <div
      data-testid="assistant-table"
      className="overflow-hidden rounded-[8px] border border-[var(--border-default)] bg-[var(--background-card)] px-[14px] text-[14px]"
    >
      {visible.map((row, i) => {
        const [name = '', value = '', ...rest] = row;
        const meta = rest.filter((c) => !EMPTY.test(c.trim())).join(' · ');
        const showValue = !EMPTY.test(value.trim());
        return (
          <div
            key={`${keyPrefix}-r${i}`}
            data-testid="assistant-table-row"
            className={`flex flex-col gap-[2px] py-[9px] ${i > 0 ? 'border-t border-[var(--border-default)]' : ''}`}
          >
            <div className="flex items-baseline justify-between gap-3">
              <span className="min-w-0 font-semibold">
                {renderInline(name, `${keyPrefix}-n${i}`)}
              </span>
              {showValue && (
                <span className="shrink-0 text-right font-semibold">
                  {renderInline(value, `${keyPrefix}-v${i}`)}
                </span>
              )}
            </div>
            {hasMeta && (
              <div className="text-[12px] text-[var(--text-content-sub-title)]">
                {meta ? renderInline(meta, `${keyPrefix}-m${i}`) : t('assistant.thread.tableNoDetails')}
              </div>
            )}
          </div>
        );
      })}
      {canCollapse && (
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
          className="-mx-[14px] flex min-h-[40px] w-[calc(100%+28px)] items-center justify-center border-t border-[var(--border-default)] text-[13px] font-semibold text-[var(--color-forest)] hover:bg-[var(--background-badge-green)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-focus)]"
        >
          {expanded
            ? t('assistant.thread.tableShowFewer')
            : t('assistant.thread.tableShowAll', { count: total })}
        </button>
      )}
    </div>
  );
}
